import React, { useState, useMemo, useEffect, useRef } from 'react';
import Icon from '../comps/Icon';
import customersSvc from '../services/customers';
import manageareas from '../services/manageareas';
import managetables from '../services/managetables';
import '../style/css/clients2026.css';

/*
 * ClientsPage — the CUSTOMER CRM (לקוחות) for the HOPPA operator app.
 *
 * Operator intent (verbatim): "נתונים על הלקוחות" — data about customers.
 * This is a polished CRM: summary stats, search + filter chips, a responsive
 * customer list (desktop table / mobile cards), and a customer PROFILE drawer
 * (side-drawer on desktop, bottom-sheet on mobile) with stats, a reservation
 * history timeline, editable notes and add/remove tags.
 *
 * The customer LIST, visit counts, last/first visit, no-shows and the profile
 * HISTORY timeline are REAL — aggregated server-side from reservations via the
 * customersList / customerHistory acts. tags, notes and block are still local
 * (no DB source yet) and clearly labeled as not-saved.
 */

/* ------------------------------------------------------------------ *
 *  MOCK DATA — replace with customers / customerProfile acts.
 *  Exported so a future data layer / tests can import the same shape.
 * ------------------------------------------------------------------ */
// Customers are loaded LIVE from the customersList act (aggregated from real reservations).
// Derive the activity status from recency + visit count (no dedicated DB column).
const deriveStatus = (visits, lastVisit) => {
  if (!lastVisit) return 'new';
  const days = Math.round((Date.now() - new Date(lastVisit).getTime()) / 86400000);
  if (visits <= 1) return 'new';
  if (days <= 60) return 'active';
  return 'inactive';
};

/* ------------------------------------------------------------------ *
 *  Constants + helpers
 * ------------------------------------------------------------------ */

// Filter chips. `test` decides membership; "recent" cut-off drives active/inactive.
const FILTERS = [
  { id: 'all', label: 'הכל', test: () => true },
  { id: 'vip', label: 'VIP', test: (c) => c.tags.includes('VIP') },
  { id: 'new', label: 'חדשים', test: (c) => c.status === 'new' },
  { id: 'inactive', label: 'לא־פעילים', test: (c) => c.status === 'inactive' },
];

const STATUS = {
  active: { label: 'פעיל', cls: 'active' },
  new: { label: 'חדש', cls: 'new' },
  inactive: { label: 'לא פעיל', cls: 'inactive' },
};

// Suggested tags for the profile tag-picker (mock taxonomy).
const TAG_SUGGESTIONS = ['VIP', 'צמחוני', 'טבעוני', 'יום-הולדת', 'משפחה', 'חדש', 'רגישות'];

const parseISO = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (s) =>
  parseISO(s).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' });

const fmtDateShort = (s) =>
  parseISO(s).toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });


const initial = (name) => (name ? name.trim().charAt(0) : '?');

const HISTORY_STATUS = {
  booked: { label: 'הוזמן', cls: 'seated' },
  arrived: { label: 'הגיע', cls: 'seated' },
  seated: { label: 'ישב', cls: 'seated' },
  cancelled: { label: 'בוטל', cls: 'cancelled' },
  'no-show': { label: 'לא הופיע', cls: 'noshow' },
};

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

const ClientsPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [toast, setToast] = useState('');
  // Area/table id→name maps (for the history timeline) + per-drawer history load.
  const [nameMaps, setNameMaps] = useState({ area: {}, table: {} });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const historyReqId = useRef(null); // stale-request guard for the drawer

  React.useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // Load real customers (aggregated from reservations) + area/table name maps.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [rows, areaList, tables] = await Promise.all([
        customersSvc.listCustomers(),
        manageareas.getAllArea(true).catch(() => []),
        managetables.getAllTables(true).catch(() => []),
      ]);
      if (!alive) return;
      const area = {};
      (Array.isArray(areaList) ? areaList : []).forEach((a) => { area[String(a.id || a.ID)] = a.name; });
      const table = {};
      (Array.isArray(tables) ? tables : []).forEach((t) => { table[String(t.id)] = t.name; });
      setNameMaps({ area, table });
      // Map each customer with SAFE DEFAULTS so the existing drawer UI never crashes.
      setCustomers((Array.isArray(rows) ? rows : []).map((c) => ({
        id: c.id,
        name: c.name || '—',
        phone: c.phone || '',
        visits: Number(c.visits) || 0,
        lastVisit: c.lastVisit || '',
        firstVisit: c.firstVisit || '',
        noShows: Number(c.noShows) || 0,
        status: deriveStatus(Number(c.visits) || 0, c.lastVisit),
        tags: [], notes: '', history: [], blocked: false,
      })));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  /* ---- summary stats (across the full roster, not the filtered view) ---- */
  const stats = useMemo(() => {
    const total = customers.length;
    const returning = customers.filter((c) => c.visits >= 5).length;
    const vip = customers.filter((c) => c.tags.includes('VIP')).length;
    const avgVisits = total
      ? Math.round(customers.reduce((s, c) => s + c.visits, 0) / total)
      : 0;
    return { total, returning, vip, avgVisits };
  }, [customers]);

  /* ---- client-side search + filter ---- */
  const filtered = useMemo(() => {
    const q = query.trim();
    const chip = FILTERS.find((f) => f.id === filter) || FILTERS[0];
    return customers
      .filter((c) => chip.test(c))
      .filter((c) => (q ? c.name.includes(q) || c.phone.includes(q) : true))
      .sort((a, b) => b.visits - a.visits);
  }, [customers, query, filter]);

  const selected = useMemo(
    () => customers.find((c) => c.id === selectedId) || null,
    [customers, selectedId]
  );

  // Load the selected customer's real reservation history (drawer timeline). Guarded against
  // stale responses — opening customer A then B must not drop A's history into B's drawer.
  useEffect(() => {
    if (!selectedId) { historyReqId.current = null; setHistory([]); setHistoryLoading(false); return; }
    historyReqId.current = selectedId;
    setHistoryLoading(true);
    (async () => {
      const rows = await customersSvc.customerHistory(selectedId);
      if (historyReqId.current !== selectedId) return; // a newer drawer opened — discard
      const mapped = (Array.isArray(rows) ? rows : []).map((h) => ({
        date: h.date,
        party: h.party,
        status: h.status || 'seated',
        area: nameMaps.area[String(h.area)] || (h.area != null && Number(h.area) ? `אזור ${h.area}` : ''),
        table: nameMaps.table[String(h.tableId)] || (h.tableId != null && Number(h.tableId) ? `שולחן ${h.tableId}` : '—'),
      }));
      setHistory(mapped);
      setHistoryLoading(false);
    })();
  }, [selectedId, nameMaps]);

  /* ---- profile mutations (client-side only) ---- */
  const updateSelected = (patch) => {
    if (!selectedId) return;
    setCustomers((prev) =>
      prev.map((c) => (c.id === selectedId ? { ...c, ...patch } : c))
    );
  };

  const addTag = (tag) => {
    if (!selected || selected.tags.includes(tag)) return;
    updateSelected({ tags: [...selected.tags, tag] });
  };
  const removeTag = (tag) => {
    if (!selected) return;
    updateSelected({ tags: selected.tags.filter((t) => t !== tag) });
  };
  // Block / unblock a client from making bookings. Client-side for now (sample data);
  // wires to a real customer-block flag + booking-time enforcement once לקוחות is DB-backed.
  const toggleBlock = () => {
    if (!selected) return;
    const next = !selected.blocked;
    updateSelected({ blocked: next });
    setToast((next ? `${selected.name} נחסם/ה מהזמנות` : `החסימה על ${selected.name} הוסרה`) + ' (הדגמה — לא נשמר בשרת)');
  };


  /* ================================================================ */

  return (
    <div className="crm-page">
      <div className="crm-demo-note">
        <Icon name="info" size={15} style={{ verticalAlign: 'middle' }} /> הלקוחות, הביקורים וההיסטוריה אמיתיים (מתוך ההזמנות). תגיות, הערות וחסימה עדיין מקומיים — לא נשמרים בשרת
      </div>

      {/* ---------------- Header ---------------- */}
      <header className="crm-header">
        <div className="crm-header-main">
          <h1 className="crm-title">לקוחות</h1>
          <p className="crm-subtitle">כל מה שחשוב לדעת על הסועדים שלכם — ביקורים, העדפות והיסטוריה</p>
        </div>
      </header>

      {/* ---------------- Summary stat strip ---------------- */}
      <div className="crm-stats">
        <div className="crm-stat">
          <span className="crm-stat-num">{stats.total}</span>
          <span className="crm-stat-lbl">סה"כ לקוחות</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat-num crm-stat-brand">{stats.returning}</span>
          <span className="crm-stat-lbl">לקוחות חוזרים</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat-num">{stats.avgVisits}</span>
          <span className="crm-stat-lbl">ממוצע ביקורים</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat-num crm-stat-vip">{stats.vip}</span>
          <span className="crm-stat-lbl">VIP</span>
        </div>
      </div>

      {/* ---------------- Search + filters ---------------- */}
      <div className="crm-toolbar">
        <div className="crm-search-wrap">
          <span className="crm-search-icon" aria-hidden="true"><Icon name="search" size={18} /></span>
          <input
            className="crm-search"
            type="search"
            placeholder="חיפוש לפי שם או טלפון…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="חיפוש לקוח"
          />
        </div>
        <div className="crm-chips" role="tablist" aria-label="סינון לקוחות">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`crm-chip ${filter === f.id ? 'is-active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------------- Customer list ---------------- */}
      <div className="crm-card">
        {loading ? (
          <div className="crm-empty">
            <p className="crm-empty-title">טוען לקוחות…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="crm-empty">
            <div className="crm-empty-emoji" aria-hidden="true"><Icon name="search" size={30} /></div>
            <p className="crm-empty-title">
              {query ? `לא נמצאו לקוחות עבור "${query}"` : (customers.length === 0 ? 'אין עדיין לקוחות — יופיעו כאן לפי ההזמנות' : 'אין לקוחות בסינון זה')}
            </p>
            <button
              type="button"
              className="crm-btn-ghost"
              onClick={() => { setQuery(''); setFilter('all'); }}
            >
              ניקוי סינון
            </button>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="crm-table">
              <thead>
                <tr>
                  <th>לקוח</th>
                  <th>טלפון</th>
                  <th>ביקורים</th>
                  <th>ביקור אחרון</th>
                  <th>אי-הגעות</th>
                  <th>תגיות</th>
                  <th>סטטוס</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const st = STATUS[c.status];
                  return (
                    <tr
                      key={c.id}
                      className={selectedId === c.id ? 'is-open' : ''}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <td data-label="לקוח">
                        <div className="crm-cust">
                          <span className={`crm-avatar ${c.tags.includes('VIP') ? 'is-vip' : ''}`}>
                            {initial(c.name)}
                          </span>
                          <span className="crm-cust-name">{c.name}</span>
                        </div>
                      </td>
                      <td data-label="טלפון"><span dir="ltr">{c.phone}</span></td>
                      <td data-label="ביקורים">{c.visits}</td>
                      <td data-label="ביקור אחרון">{fmtDate(c.lastVisit)}</td>
                      <td data-label="אי-הגעות">{c.noShows || 0}</td>
                      <td data-label="תגיות">
                        <span className="crm-tags">
                          {c.tags.length === 0 && <span className="crm-tag-none">—</span>}
                          {c.tags.map((t) => (
                            <span key={t} className={`crm-tag ${t === 'VIP' ? 'is-vip' : ''}`}>{t}</span>
                          ))}
                        </span>
                      </td>
                      <td data-label="סטטוס">
                        <span className={`crm-badge badge-${st.cls}`}>{st.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile cards */}
            <ul className="crm-cards">
              {filtered.map((c) => {
                const st = STATUS[c.status];
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`crm-card-row ${selectedId === c.id ? 'is-open' : ''}`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <span className={`crm-avatar ${c.tags.includes('VIP') ? 'is-vip' : ''}`}>
                        {initial(c.name)}
                      </span>
                      <span className="crm-card-body">
                        <span className="crm-card-top">
                          <span className="crm-cust-name">{c.name}</span>
                          {c.blocked
                            ? <span className="crm-badge badge-blocked">חסום</span>
                            : <span className={`crm-badge badge-${st.cls}`}>{st.label}</span>}
                        </span>
                        <span className="crm-card-meta">
                          <span dir="ltr">{c.phone}</span>
                          <span>· {c.visits} ביקורים</span>
                          {c.lastVisit && <span>· {fmtDate(c.lastVisit)}</span>}
                        </span>
                        {c.tags.length > 0 && (
                          <span className="crm-tags">
                            {c.tags.map((t) => (
                              <span key={t} className={`crm-tag ${t === 'VIP' ? 'is-vip' : ''}`}>{t}</span>
                            ))}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* ---------------- Customer profile drawer ---------------- */}
      {selected && (
        <>
          <div className="crm-scrim" onClick={() => setSelectedId(null)} />
          <aside className="crm-drawer" role="dialog" aria-label={`פרופיל לקוח — ${selected.name}`}>
            <div className="crm-drawer-head">
              <div className="crm-drawer-id">
                <span className={`crm-avatar crm-avatar-lg ${selected.tags.includes('VIP') ? 'is-vip' : ''}`}>
                  {initial(selected.name)}
                </span>
                <div className="crm-drawer-idtext">
                  <h2 className="crm-drawer-name">
                    {selected.name}
                    {selected.blocked && <span className="crm-blocked-badge">חסום</span>}
                  </h2>
                  <a className="crm-drawer-phone" href={`tel:${selected.phone}`} dir="ltr">{selected.phone}</a>
                  <div className="crm-tags">
                    {selected.tags.map((t) => (
                      <span key={t} className={`crm-tag is-removable ${t === 'VIP' ? 'is-vip' : ''}`}>
                        {t}
                        <button
                          type="button"
                          className="crm-tag-x"
                          onClick={() => removeTag(t)}
                          aria-label={`הסרת תגית ${t}`}
                        >✕</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="crm-drawer-close"
                onClick={() => setSelectedId(null)}
                aria-label="סגירה"
              >✕</button>
            </div>

            <div className="crm-drawer-body">
              {/* Key stats */}
              <div className="crm-pstats">
                <div className="crm-pstat">
                  <span className="crm-pstat-num">{selected.visits}</span>
                  <span className="crm-pstat-lbl">ביקורים</span>
                </div>
                <div className="crm-pstat">
                  <span className="crm-pstat-num">{selected.lastVisit ? fmtDateShort(selected.lastVisit) : '—'}</span>
                  <span className="crm-pstat-lbl">ביקור אחרון</span>
                </div>
                <div className="crm-pstat">
                  <span className="crm-pstat-num">{selected.firstVisit ? fmtDateShort(selected.firstVisit) : '—'}</span>
                  <span className="crm-pstat-lbl">לקוח מאז</span>
                </div>
                <div className="crm-pstat">
                  <span className={`crm-pstat-num ${selected.noShows ? 'is-danger' : ''}`}>
                    {selected.noShows}
                  </span>
                  <span className="crm-pstat-lbl">אי-הגעות</span>
                </div>
              </div>

              {/* Add-tag picker */}
              <section className="crm-section">
                <h3 className="crm-section-title">תגיות</h3>
                <div className="crm-tag-picker">
                  {TAG_SUGGESTIONS.filter((t) => !selected.tags.includes(t)).map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="crm-tag-add"
                      onClick={() => addTag(t)}
                    >
                      ＋ {t}
                    </button>
                  ))}
                  {TAG_SUGGESTIONS.every((t) => selected.tags.includes(t)) && (
                    <span className="crm-tag-none">כל התגיות כבר משויכות</span>
                  )}
                </div>
              </section>

              {/* Reservation history timeline (real, loaded from customerHistory) */}
              <section className="crm-section">
                <h3 className="crm-section-title">היסטוריית ביקורים</h3>
                {historyLoading ? (
                  <div className="crm-tl-empty">טוען היסטוריה…</div>
                ) : history.length === 0 ? (
                  <div className="crm-tl-empty">אין היסטוריית ביקורים</div>
                ) : (
                <ol className="crm-timeline">
                  {history.map((h, i) => {
                    const hs = HISTORY_STATUS[h.status] || HISTORY_STATUS.seated;
                    return (
                      <li key={i} className={`crm-tl-item tl-${hs.cls}`}>
                        <span className="crm-tl-dot" aria-hidden="true" />
                        <div className="crm-tl-body">
                          <div className="crm-tl-top">
                            <span className="crm-tl-date">{fmtDateShort(h.date)}</span>
                            <span className={`crm-badge badge-h-${hs.cls}`}>{hs.label}</span>
                          </div>
                          <div className="crm-tl-meta">
                            <span><Icon name="users" size={14} style={{ verticalAlign: 'middle' }} /> {h.party}</span>
                            {h.area && <span>{h.area}</span>}
                            <span className="crm-tl-table">{h.table}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                )}
              </section>

              {/* Editable notes */}
              <section className="crm-section">
                <h3 className="crm-section-title">הערות</h3>
                <textarea
                  className="crm-notes"
                  placeholder="הוספת הערה על הלקוח (העדפות, רגישויות, בקשות מיוחדות)…"
                  value={selected.notes}
                  onChange={(e) => updateSelected({ notes: e.target.value })}
                  aria-label="הערות על הלקוח"
                  rows={3}
                />
              </section>
            </div>

            {/* Actions */}
            <div className="crm-drawer-actions">
              <a className="crm-act crm-act-call" href={`tel:${selected.phone}`}><Icon name="phone" size={16} style={{ verticalAlign: 'middle' }} /> התקשרות</a>
              {/* Mock actions — future: reservationCreate / customerUpdate acts. */}
              <button type="button" className="crm-act crm-act-book" onClick={() => setToast('הזמנה חדשה מלקוח תתווסף בקרוב (הדגמה — לא בוצעה פעולה)')}>
                ＋ הזמנה חדשה
              </button>
              <button type="button" className="crm-act crm-act-edit" onClick={() => setToast('עריכת פרטי לקוח תתווסף בקרוב (הדגמה)')}>
                עריכה
              </button>
              <button
                type="button"
                className={`crm-act ${selected.blocked ? 'crm-act-unblock' : 'crm-act-block'}`}
                onClick={toggleBlock}
              >
                <Icon name="ban" size={16} style={{ verticalAlign: 'middle' }} /> {selected.blocked ? 'בטל חסימה' : 'חסום להזמנה'}
              </button>
            </div>
          </aside>
        </>
      )}

      {toast && <div className="crm-toast" role="status">{toast}</div>}
    </div>
  );
};

export default ClientsPage;
